import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

interface ConfirmModalProps {
    title: string;
    message: React.ReactNode;
    confirmLabel: string;
    /** Font Awesome icon name for the header, e.g. 'fa-check-double'. */
    icon: string;
    busy?: boolean;
    onConfirm: () => void;
    onCancel: () => void;
}

/** In-app replacement for window.confirm, styled like the other admin modals. */
const ConfirmModal: React.FC<ConfirmModalProps> = ({ title, message, confirmLabel, icon, busy = false, onConfirm, onCancel }) => {
    const { t } = useTranslation();

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && !busy) onCancel();
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [busy, onCancel]);

    return (
        <div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onCancel(); }}
        >
            <div role="dialog" aria-modal="true" aria-labelledby="confirm-modal-title" className="bg-white rounded-2xl shadow-2xl max-w-md w-full border border-slate-200 overflow-hidden">
                <div className="bg-gradient-to-r from-cyan-600 to-blue-600 px-4 md:px-6 py-3 md:py-4 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center flex-shrink-0">
                        <i className={`fa-solid ${icon} text-white text-lg`}></i>
                    </div>
                    <h3 id="confirm-modal-title" className="text-xl font-bold text-white">{title}</h3>
                </div>
                <div className="p-4 md:p-6 bg-slate-50 text-sm text-slate-600">{message}</div>
                <div className="px-4 md:px-6 py-3 md:py-4 bg-white border-t border-slate-200 flex gap-2 md:gap-3">
                    <button
                        onClick={onCancel}
                        disabled={busy}
                        className="flex-1 px-4 py-2.5 bg-cyan-50 hover:bg-cyan-100 text-cyan-700 rounded-lg font-medium transition-colors border border-cyan-200 disabled:opacity-50"
                    >
                        {t('common.cancel')}
                    </button>
                    <button
                        onClick={onConfirm}
                        disabled={busy}
                        autoFocus
                        className="flex-1 px-4 py-2.5 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white rounded-lg font-bold transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <i className={`fa-solid ${busy ? 'fa-spinner fa-spin' : icon} mr-2`}></i>
                        {confirmLabel}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ConfirmModal;
